import { Utils, ObjectHelper } from '../../../../helpers'
import { IPropertyMappingConfig } from './'

export class EntityMapping {

  private _propertyMappings: IPropertyMappingConfig

  constructor(
    private _className: string,
    private _jsonType: string,
    private _utils: Utils,
    private _objectHelper: ObjectHelper
  ) {}

  public get entityNameForClass() {
    return this._className
  }

  public get entityNameForJson() {
    return this._jsonType
  }

  // TODO [GitHub Issue]: Perhaps is better to create an object (mappingConfig) that handles the normalization and 'swapping' of
  // the mapping. Something like
  // mappingConfig.hasMany(pathNodes, opts)
  // mappingConfig.hasOne(task, opts)
  // This doesn't solve the problem of having to run this logic for every test. Having this logic outside in the
  // mappingConfig class would
  public setCustomMapping(mapping: IPropertyMappingConfig) {
    this._propertyMappings = this._normalizeMapping(mapping)
    this._propertyMappings = this._registerJsonPropertyMapping(this._propertyMappings)
  }

  public getClassTransformedValue(jsonPropertyName: string, jsonPropertyValue: any) {
    const propertyConfig = this._getPropertyConfig(jsonPropertyName, this._classPropertyNameStrategy)
    let value = propertyConfig.transform.toModel(jsonPropertyValue)

    if (typeof value === 'undefined' || value === null || value.links) {
      value = propertyConfig.isCollection ? [] : undefined
    }
    return value
  }

  public getJsonTransformedValue(classPropertyName: string, classPropertyValue: any) {
    const propertyConfig = this._getPropertyConfig(classPropertyName, this._jsonPropertyNameStrategy)
    return propertyConfig.transform.toJson(classPropertyValue)
  }

  public getJsonPropertyName(
    classPropertyName: string,
    defaultNameStrategy: (property) => string = this._jsonPropertyNameStrategy
  ): string {
    classPropertyName = this._classPropertyNameStrategy(classPropertyName)
    return this._getPropertyConfig(classPropertyName, defaultNameStrategy).mapsTo
  }

  public getClassPropertyName(jsonPropertyName: string): string {
    return this._getPropertyConfig(jsonPropertyName, this._classPropertyNameStrategy).mapsTo
  }

  public isClassPropertyCollection(jsonPropertyName: string): boolean {
    return this._getPropertyConfig(jsonPropertyName, this._classPropertyNameStrategy).isCollection
  }

  public hasCustomJsonMapping(jsonPropertyName: string): boolean {
    return !!this._propertyMappings[jsonPropertyName] &&
      !!this._propertyMappings[jsonPropertyName]['transform'] &&
      !!this._propertyMappings[jsonPropertyName]['transform']['toJson']
  }

  private _normalizeMapping(mapping: any): IPropertyMappingConfig {
    for (const propertyName in mapping) {
      mapping[propertyName] = Object.assign(
        this._getDefaultPropertyMappingConfig(this._jsonPropertyNameStrategy(propertyName)),
        mapping[propertyName]
      )
    }
    return mapping
  }

  private _registerJsonPropertyMapping(
    mapping: IPropertyMappingConfig
  ): IPropertyMappingConfig {
    for (const propertyName in mapping) {
      const swapped = mapping[mapping[propertyName]['mapsTo']] = Object.assign({}, mapping[propertyName])
      swapped['mapsTo'] = propertyName
    }
    return mapping
  }

  private _getPropertyConfig(propertyName: string, nameStrategy: (prop, utils) => string) {
    const propertyMapping = this._propertyMappings[propertyName] || {}
    return this._objectHelper.mergeDeep({}, this._getDefaultPropertyMappingConfig(nameStrategy.call(this, propertyName)), propertyMapping)
  }

  private _jsonPropertyNameStrategy(property) {
    return this._utils.dashCase(property)
  }

  private _classPropertyNameStrategy(property) {
    return this._utils.camelCase(property)
  }

  private _getDefaultPropertyMappingConfig(propertyName: string): {
    mapsTo: string,
    type: string,
    isCollection: boolean
    transform: { toModel: (val) => any, toJson: (val) => any }
  } {
    return {
      mapsTo: propertyName,
      type: this._utils.pascalCase(propertyName),
      isCollection: this._utils.isPlural(propertyName),
      transform: {
        toModel: (value) => value,
        toJson: (value) => value
      }
    }
  }
}



// WEBPACK FOOTER //
// ./node_modules/@mprep/shared/lib/model-repository/backends/entity-mapping-manager/entity-mapping/entity-mapping.ts